import { CardMinimalTileUi } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.minimal";
import { cardContentKindDrillKind } from "@-/dev.improve.haniverse.lib.cards.card-content";
import type { LearnSummaryDueReviewCard } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import type { RevealedMorphemeGlyphTileGrid } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { userCardSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import filledArray from "@-/com.npmjs.filled-array";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { View } from "@-/com.npmjs.react-native";
import { type ReactNode, useCallback, useMemo, useState } from "react";

type NavigateToMorpheme = (morpheme: string) => Promise<unknown>;

function DueReviewCardTile({
  card,
  onNavigate,
  size,
  tileData,
}: {
  card: LearnSummaryDueReviewCard;
  onNavigate: NavigateToMorpheme;
  size: number | undefined;
  tileData: RevealedMorphemeGlyphTileGrid["tileData"];
}) {
  const selectionItems = useMemo(
    () => [
      userCardSelectionItem({
        contentKind: card.cardContentKind,
        front: card.subject,
        userCardId: card.userCardId,
      }),
    ],
    [card],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [pendingCount, setPendingCount] = useState(0);
  const handlePress = useCallback(() => {
    if (consumePress()) {
      return;
    }
    setPendingCount((count) => count + 1);
    const settle = () => setPendingCount((count) => count - 1);
    // Both branches release the tile's loading state; the navigator owns error reporting.
    void onNavigate(card.subject).then(settle, settle);
  }, [card.subject, consumePress, onNavigate]);

  const cardType = cardContentKindDrillKind(card.cardContentKind);

  return (
    <CardMinimalTileUi
      cardType={cardType}
      glyph={card.subject}
      isLoading={pendingCount > 0}
      isSelected={isSelected}
      onLongPress={onLongPress}
      onPress={handlePress}
      primaryPinyin={tileData.getSlice(card.subject).primaryPinyin}
      size={size}
      stage={tileData.getStage(card.subject)}
      testID={`learn-summary-due-review-card-${card.userCardId}`}
      // Tone colors mark pronunciation cards; meaning glyphs stay plain so the two read apart.
      uncolored={cardType === "meaning"}
    />
  );
}

/**
 * One row of the summary's due-review grid: a minimal card tile (glyph + card-type icon) per due
 * card, fed its stage and pinyin from the row's paged grapheme `tileData`. Mounts ONE panels
 * navigator for the row instead of one per tile.
 */
export function LearnSummaryDueReviewCardRow({
  availableWidth,
  cards,
  columns,
  gap,
  href,
  tileData,
  trailingCell,
}: {
  /** The row's width when known without measuring, so tiles render at final size on first commit. */
  availableWidth: number | undefined;
  cards: readonly LearnSummaryDueReviewCard[];
  columns: number;
  gap: number;
  href: string;
  tileData: RevealedMorphemeGlyphTileGrid["tileData"];
  /** An optional final cell after the cards (the collapsed grid's "+N" tile). */
  trailingCell?: ReactNode;
}) {
  const pushMutateAsync = useMorphemePanelsHrefNavigator().push.mutateAsync;
  const onNavigate = useCallback<NavigateToMorpheme>(
    (morpheme) => pushMutateAsync({ morpheme, prefix: { href, params: { view: "mnemonics" } } }),
    [href, pushMutateAsync],
  );
  const tileSize =
    availableWidth === undefined
      ? undefined
      : Math.floor((availableWidth - gap * (columns - 1)) / columns);
  const filledCellCount = arrayLength(cards) + (trailingCell == null ? 0 : 1);

  return (
    <MorphemeTilePresentationProvider>
      <View className="flex flex-row" style={{ gap }}>
        {cards.map((card) => (
          <View className="flex-1 items-center justify-center" key={card.userCardId}>
            <DueReviewCardTile
              card={card}
              onNavigate={onNavigate}
              size={tileSize}
              tileData={tileData}
            />
          </View>
        ))}
        {trailingCell == null ? null : (
          <View className="flex-1 items-center justify-center">
            <View className="aspect-square w-full">{trailingCell}</View>
          </View>
        )}
        {filledArray(
          (placeholderIndex) => (
            <View className="flex-1" key={`placeholder-${placeholderIndex}`} />
          ),
          Math.max(0, columns - filledCellCount),
        )}
      </View>
    </MorphemeTilePresentationProvider>
  );
}
